import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { db, auth } from '../../lib/firebase';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { markAllNotificationsRead, markNotificationRead, type NotificationItem } from '../../services/notifications';

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setLoading(true);
    let unsub: any;
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      unsub = onSnapshot(q, (snap) => {
        const items: NotificationItem[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setNotifications(items);
        setLoading(false);
      }, (err) => {
        console.error('Notifications subscribe failed', err);
        setError('Failed to load notifications');
        setLoading(false);
      });
    } catch (err) {
      console.warn('orderBy fallback in notifications subscribe');
      const q2 = query(
        collection(db, 'notifications'),
        where('userId', '==', user?.uid || '')
      );
      unsub = onSnapshot(q2, (snap) => {
        const items: NotificationItem[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        // client-side sort by createdAt
        items.sort((a: any, b: any) => {
          const ta = a.createdAt?.seconds || 0;
          const tb = b.createdAt?.seconds || 0;
          return tb - ta;
        });
        setNotifications(items);
        setLoading(false);
      }, (err) => {
        console.error('Notifications subscribe failed (fallback)', err);
        setError('Failed to load notifications');
        setLoading(false);
      });
    }

    return () => { if (typeof unsub === 'function') unsub(); };
  }, []);

  const handleMarkAllRead = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      await markAllNotificationsRead(user.uid);
    } catch (err) {
      console.error('markAllNotificationsRead failed', err);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await markNotificationRead(id);
    } catch (err) {
      console.error('markNotificationRead failed', err);
    }
  };

  const renderIcon = (type?: string) => {
    switch (type) {
      case 'viewer': return <i className="ri-eye-line text-blue-600"></i>;
      case 'order': return <i className="ri-shopping-bag-3-line text-purple-600"></i>;
      case 'wallet': return <i className="ri-wallet-3-line text-green-600"></i>;
      default: return <i className="ri-notification-3-line text-teal-600"></i>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <button
            onClick={handleMarkAllRead}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 text-sm font-medium"
          >
            <i className="ri-check-double-line"></i>
            Mark all as read
          </button>
        </div>

        {loading && (
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <i className="ri-loader-4-line animate-spin"></i>
              Loading notifications...
            </div>
          </div>
        )}

        {error && (
          <div className="p-4 mb-4 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm">
            {error}
          </div>
        )}

        {(!loading && notifications.length === 0) && (
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="text-gray-700">No notifications yet.</div>
            <div className="mt-2 text-sm text-gray-600">As buyers view your listings or place orders, you’ll see updates here.</div>
          </div>
        )}

        <ul className="space-y-3">
          {notifications.map((n) => {
            const created = (n as any).createdAt?.seconds
              ? new Date((n as any).createdAt.seconds * 1000).toLocaleString()
              : '—';
            const isUnread = !(n as any).read;
            return (
              <li key={n.id} className={`p-4 bg-white rounded-lg border ${isUnread ? 'border-teal-200' : 'border-gray-200'} shadow-sm`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {renderIcon((n as any).type)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{(n as any).title || 'Notification'}</div>
                      <div className="text-sm text-gray-700">{(n as any).body}</div>
                      <div className="text-xs text-gray-500 mt-1">{created}</div>
                      {(n as any).listingId && (
                        <div className="mt-2">
                          <Link to={`/product/${(n as any).listingId}`} className="text-sm text-teal-700 hover:text-teal-800">View listing</Link>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUnread && (
                      <button
                        onClick={() => handleMarkRead(n.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs"
                      >
                        <i className="ri-check-line"></i>
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </main>
      <Footer />
    </div>
  );
};

export default NotificationsPage;